import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const packages = [
  {
    name: 'Matinee Haven',
    tagline: 'Foundational Luxury',
    roomSize: "11' x 15'",
    description: 'Transform your space into a cinematic oasis with our foundational luxury package. Perfect for those seeking elegance and functionality.',
    features: [
      '4 premium theater chairs',
      '7.1 surround sound system',
      '4 theater sconce lights & electrical setup (Certified Electrician)',
      'Custom ceiling design (LED, star lights, or theater-style)',
      'Custom media console',
      'High-quality projector and projector screen',
      'Theater-grade carpet or laminate flooring',
      'Design rendering and material presentation',
    ],
    image: 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/3a6d84ac7550df04d4b04e88b8c82716a1617b76aa839605b1c36076ce7335f3.jpeg',
  },
  {
    name: 'Premiere Lounge',
    tagline: 'Enhanced Experience',
    roomSize: "24' x 15'",
    description: 'Elevate your home theater experience with added luxury and enhanced features for a truly premium space.',
    features: [
      '8-10 premium theater chairs (Upgrades)',
      'Custom velvet theater bench',
      'Custom mini bar or concession stand',
      'Upgraded projector and LED projector screen',
      'Custom theater sound dampening panels',
      '7.2 surround sound system',
      '6 theater lights & electrical setup',
      'Custom ceiling design (LED, star lights, or theater-style)',
      'Custom media console',
      'Theater-grade carpet or laminate flooring',
      'Design rendering and material presentation',
    ],
    featured: true,
    image: 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/b41f58f8638daf4f0a74a3a61c9360454c8cbadf1d35863f161e6285e9e58caa.jpeg',
  },
  {
    name: "Director's Suite",
    tagline: 'The Pinnacle of Luxury',
    roomSize: "30' x 18'",
    description: 'The pinnacle of luxury and customization, designed for those who demand the extraordinary.',
    features: [
      '12-16 premium theater chairs (upgraded, popcorn/tablet holder)',
      'Custom velvet theater bench',
      'One-of-a-kind custom theater artwork (Featured Artist)',
      '9.2 surround sound system',
      'Mini bar or concession stand',
      'Theater preview screen',
      'Upgraded projector and screen',
      'Custom theater sound dampening panels',
      '8-10 theater lights & electrical setup',
      'Custom ceiling design (LED, star lights, or theater-style)',
      'Custom media console',
      'Theater-grade carpet or laminate flooring',
      'Design rendering and material presentation',
    ],
    image: 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/f1464e1a03647541501b4322cdd142e5c9125c6856ba833dfc64626ab8c6f276.jpeg',
  },
];

export default function PackagesPage() {
  const { c } = useSiteContent();
  return (
    <div className="min-h-screen pt-24" data-testid="packages-page">
      {/* Hero */}
      <section className="py-20 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">Our Packages</p>
            <h1 className="font-playfair text-4xl lg:text-5xl text-white mb-4">
              {c('packages.title', 'Luxury Tailored to Your')} <span className="text-[#D4AF37]">{c('packages.title_accent', 'Lifestyle')}</span>
            </h1>
            <p className="text-white/40 font-manrope text-lg max-w-2xl leading-relaxed">
              {c('packages.subtitle', "We've designed tiered packages that blend cutting-edge technology, comfort, and style — so whether you're creating an intimate retreat or a grand cinematic experience, there's a perfect fit.")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 space-y-16">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`
                grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 p-6 lg:p-10 rounded-sm
                ${pkg.featured
                  ? 'bg-[#D4AF37]/[0.03] border border-[#D4AF37]/20'
                  : 'bg-[#111113] border border-white/5'
                }
              `}
              data-testid={`package-${pkg.name.toLowerCase().replace(/[\s']/g, '-')}`}
            >
              {/* Image */}
              <div className={`aspect-video rounded-sm overflow-hidden ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="flex flex-col justify-center">
                {pkg.featured && (
                  <span className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-bold mb-2 inline-block">Most Popular</span>
                )}
                <h2 className="font-playfair text-3xl text-white mb-1">{pkg.name}</h2>
                <p className="text-[#D4AF37] font-manrope text-sm tracking-wider mb-2">{pkg.tagline} — Room Size: {pkg.roomSize}</p>
                <p className="text-white/40 font-manrope text-sm leading-relaxed mb-6">{pkg.description}</p>

                <div className="space-y-2 mb-8">
                  {pkg.features.map((f, fi) => (
                    <div key={fi} className="flex items-start gap-3">
                      <Check size={14} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span className="text-white/60 text-sm font-manrope">{f}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/book"
                  className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black font-manrope font-bold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#C5A059] transition-all duration-300 w-full sm:w-auto"
                  data-testid={`book-${pkg.name.toLowerCase().replace(/[\s']/g, '-')}`}
                >
                  Book a Consultation <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Custom Design CTA */}
      <section className="py-20 bg-[#0a0a0b]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-3xl text-white mb-4">Want Something Completely Custom?</h2>
            <p className="text-white/40 font-manrope max-w-lg mx-auto mb-8">
              Use our interactive Room Designer to configure every detail of your dream theater and request a personalized quote.
            </p>
            <Link
              to="/design-your-theater"
              className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-manrope font-semibold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
              data-testid="custom-design-cta"
            >
              Open Room Designer <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
